import { ShirtIcon } from "@xola/icons";
import { Button, Popover, SubmitButton } from "@xola/ui-kit";
import { ICellRendererParams } from "ag-grid-community";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Merchandise } from "types";
import { MerchandiseOrdersRowData } from "./useMerchandiseOrdersData";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import {
    getInitialVariantFulfillMap,
    getUpdateFulfillmentPayload,
} from "@/components/PurchaseModification/MerchandiseFulfillment/merchandiseFulfillmentHelper";
import { MerchandiseFulfillmentTable } from "@/components/PurchaseModification/MerchandiseFulfillment/MerchandiseFulfillmentTable";
import { ReportDamagedMerchandiseModal } from "@/components/PurchaseModification/MerchandiseFulfillment/ReportDamagedMerchandiseModal";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { isInventoryTracked, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { getMerchandisePurchaseItems } from "@/models/MerchandisePurchaseItem";

type VariantFulfillMap = Record<string, number>;

export const FulfillmentCellRenderer = ({ data }: ICellRendererParams<MerchandiseOrdersRowData>) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [toFulfillMap, setToFulfillMap] = useState<VariantFulfillMap>({});

    if (!data?.purchase) {
        return <span>{data?.itemsFulfilled ?? "0/0"}</span>;
    }

    return (
        <FulfillmentPopover
            data={data}
            isPopoverOpen={isPopoverOpen}
            setIsPopoverOpen={setIsPopoverOpen}
            toFulfillMap={toFulfillMap}
            setToFulfillMap={setToFulfillMap}
        />
    );
};

interface FulfillmentPopoverProps {
    data: MerchandiseOrdersRowData;
    isPopoverOpen: boolean;
    setIsPopoverOpen: (open: boolean) => void;
    toFulfillMap: VariantFulfillMap;
    setToFulfillMap: (map: VariantFulfillMap | ((previous: VariantFulfillMap) => VariantFulfillMap)) => void;
}

const FulfillmentPopover = ({
    data,
    isPopoverOpen,
    setIsPopoverOpen,
    toFulfillMap,
    setToFulfillMap,
}: FulfillmentPopoverProps) => {
    const purchase = data.purchase;
    const invalidate = useInvalidate();
    const reportDamageModal = useModal();
    const queryClient = useQueryClient();
    const showReportDamage = useMemo(
        () =>
            getMerchandisePurchaseItems(purchase).some((item) => {
                const product = queryClient.getQueryData<Merchandise>([MERCHANDISE_CACHE_KEY, item.product.id]);
                return product ? isInventoryTracked(product) : false;
            }),
        [purchase, queryClient],
    );

    const { mutateAsync: updateFulfillment, isLoading } = useMutation(async (payload: any) => {
        return api.patch(`/purchases/${purchase.id}`, payload);
    });

    const [fulfilled, total] = data.itemsFulfilled?.split("/") ?? ["0", "0"];
    const isFullyFulfilled = fulfilled === total;
    const shouldInitializeFulfillMap = isPopoverOpen && Object.keys(toFulfillMap).length === 0;
    const initialVariantFulfillMap = useMemo(() => getInitialVariantFulfillMap(purchase), [purchase]);

    useEffect(() => {
        if (shouldInitializeFulfillMap) {
            setToFulfillMap(initialVariantFulfillMap);
        }
    }, [shouldInitializeFulfillMap, initialVariantFulfillMap, setToFulfillMap]);

    const handlePopoverClose = () => {
        setIsPopoverOpen(false);
        setToFulfillMap({});
    };

    const handleToFulfillChange = (lineItemId: string, value: number) => {
        setToFulfillMap((previous) => ({ ...previous, [lineItemId]: value }));
    };

    const handleApplyChanges = async () => {
        const payload = getUpdateFulfillmentPayload(purchase, toFulfillMap);
        await updateFulfillment(payload);
        invalidate("purchases");
        handlePopoverClose();
    };

    const handleOpenPopover = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setIsPopoverOpen(true);
        },
        [setIsPopoverOpen],
    );

    const hasChanges = Object.values(toFulfillMap).some((value) => value > 0);
    const appendTarget = useMemo(() => document.querySelector("#merchandise-dashboard-list") ?? document.body, []);

    return (
        <>
            <Popover
                visible={isPopoverOpen}
                placement="bottom"
                className="min-w-150"
                appendTo={appendTarget}
                onClickOutside={handlePopoverClose}
            >
                <button
                    type="button"
                    className="flex cursor-pointer items-center space-x-1 hover:text-primary"
                    onClick={handleOpenPopover}
                >
                    <span
                        className={clsx("mr-1 h-2 w-2 rounded-full", isFullyFulfilled ? "bg-success" : "bg-warning")}
                    />
                    <ShirtIcon />
                    <span className="underline">{data.itemsFulfilled}</span>
                </button>

                <Popover.Content>
                    <div className="rounded-lg bg-white pb-4">
                        <div className="flex h-full flex-col">
                            <div className="mb-4">
                                <MerchandiseFulfillmentTable
                                    purchase={purchase}
                                    toFulfillMap={toFulfillMap}
                                    onToFulfillChange={handleToFulfillChange}
                                />
                            </div>

                            <div className="flex px-4">
                                {showReportDamage && (
                                    <Button
                                        size="small"
                                        color="danger"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handlePopoverClose();
                                            reportDamageModal.show();
                                        }}
                                    >
                                        Report Damage
                                    </Button>
                                )}

                                <div className="ml-auto flex space-x-2">
                                    <SecondaryButton size="small" onClick={handlePopoverClose}>
                                        Cancel
                                    </SecondaryButton>
                                    <SubmitButton
                                        size="small"
                                        isLoading={isLoading}
                                        disabled={!hasChanges || isLoading}
                                        onClick={handleApplyChanges}
                                    >
                                        Apply
                                    </SubmitButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </Popover.Content>
            </Popover>
            <ReportDamagedMerchandiseModal
                purchase={purchase}
                isOpen={reportDamageModal.isVisible}
                onClose={reportDamageModal.hide}
            />
        </>
    );
};

export const fulfillmentCellValueGetter = (params: ICellRendererParams<MerchandiseOrdersRowData>) => {
    return params.data?.itemsFulfilled ?? "0/0";
};
